import { useMemo } from 'react';
import { useStore } from '../../state/store';
import { backgroundStyle } from './backgrounds';
import { useNovelStore } from './novelStore';
import { Silhouette } from './Silhouette';

/**
 * サウンドノベル再生。プレイ中は AI を呼ばない。
 * AppShell 外で全画面表示し、没入感を優先する。
 */
export function AiNovelPlayerScreen() {
  const setView = useStore((s) => s.setView);
  const game = useNovelStore((s) => s.game);
  const sceneId = useNovelStore((s) => s.sceneId);
  const choose = useNovelStore((s) => s.choose);
  const restart = useNovelStore((s) => s.restart);
  const backToHub = useNovelStore((s) => s.backToHub);

  const scene = useMemo(() => game?.scenes.find((s) => s.id === sceneId) ?? null, [game, sceneId]);
  const chars = useMemo(() => {
    if (!game || !scene) return [];
    return scene.characters
      .map((id) => game.characters.find((c) => c.id === id))
      .filter((c): c is NonNullable<typeof c> => Boolean(c));
  }, [game, scene]);

  if (!game || !scene) {
    return (
      <div className="flex h-full items-center justify-center bg-base-950 px-4">
        <div className="card max-w-sm p-5 text-center">
          <p className="text-sm text-slate-300">ゲームデータがありません。</p>
          <button
            type="button"
            className="btn-primary mt-4 w-full"
            onClick={() => {
              backToHub();
              setView('games');
            }}
          >
            EmYu-Ba Games へ戻る
          </button>
        </div>
      </div>
    );
  }

  const ending = Boolean(scene.isEnding) || !(scene.choices && scene.choices.length);

  return (
    <div className="relative flex h-full w-full justify-center bg-black">
      <div className="relative flex h-full w-full max-w-lg flex-col overflow-hidden md:max-w-xl landscape:max-w-3xl">
        {/* 舞台 */}
        <div className="relative min-h-0 flex-1" style={backgroundStyle(scene.background)}>
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_40%,rgba(0,0,0,0.45)_100%)]" />

          <div className="absolute left-3 top-[max(0.75rem,calc(var(--safe-top)+0.35rem))] z-10 flex gap-2 pointer-events-auto">
            <button
              type="button"
              className="btn-ghost !min-h-0 !rounded-lg !bg-black/40 !px-2.5 !py-1.5 !text-[0.65rem] backdrop-blur-sm"
              onClick={() => {
                backToHub();
                setView('games');
              }}
            >
              Games
            </button>
            <button
              type="button"
              className="btn-ghost !min-h-0 !rounded-lg !bg-black/40 !px-2.5 !py-1.5 !text-[0.65rem] backdrop-blur-sm"
              onClick={() => setView('home')}
            >
              Home
            </button>
            <button
              type="button"
              className="btn-ghost !min-h-0 !rounded-lg !bg-black/40 !px-2.5 !py-1.5 !text-[0.65rem] backdrop-blur-sm"
              onClick={restart}
            >
              Restart
            </button>
          </div>

          <div className="absolute inset-x-0 bottom-[38%] flex items-end justify-center gap-4 px-4 sm:bottom-[34%]">
            {chars.length === 0 ? (
              <div className="h-28" aria-hidden />
            ) : (
              chars.slice(0, 2).map((c) => <Silhouette key={c.id} kind={c.silhouette} label={c.name} />)
            )}
          </div>
        </div>

        {/* テキスト／選択肢 */}
        <div
          className="shrink-0 border-t border-base-600/80 bg-[#0a0e16]/95 px-4 pt-3 backdrop-blur-md"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))' }}
        >
          <p className="mb-1 truncate text-[0.65rem] text-slate-500">{game.title}</p>
          <p className="min-h-[4.5rem] text-sm leading-relaxed text-slate-100">{scene.text}</p>

          <div className="mt-3 space-y-2">
            {ending ? (
              <>
                <p className="font-display text-center text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-accent">
                  Ending
                </p>
                <button type="button" className="btn-primary w-full" onClick={restart}>
                  もう一度最初から
                </button>
                <button
                  type="button"
                  className="btn-ghost w-full"
                  onClick={() => {
                    backToHub();
                    setView('games');
                  }}
                >
                  EmYu-Ba Games へ戻る
                </button>
              </>
            ) : (
              (scene.choices ?? []).map((ch) => (
                <button
                  key={`${ch.nextScene}-${ch.text}`}
                  type="button"
                  className="btn-ghost w-full !justify-start !py-3 text-left text-sm"
                  onClick={() => choose(ch.nextScene)}
                >
                  {ch.text}
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
